import React, { useState } from 'react';
import { Phone, Mail, MapPin, Building, Users, Receipt, ClipboardCheck, CheckCircle2, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import toast from 'react-hot-toast';
import { formatCurrency, formatPhoneNumber } from '../../utils/formatters';
import { WorkOrderDocumentationChecklist } from '../../types';
import { getDocumentationItemClasses } from '../../utils/workOrderDocumentation';
import { useSubscription } from '../../contexts/SubscriptionContext';

interface Vendor {
  vendorId: number;
  vendorName: string;
  vendorAddress?: string;
  vendorCity?: string;
  vendorState?: string;
  vendorZipCode?: string;
  vendorPhone?: string;
  vendorEmail?: string;
  isPrimary: boolean;
}

interface WorkOrder {
  id: number;
  clientName: string;
  clientLocationName?: string;
  clientCity?: string;
  clientState?: string;
  clientZipCode?: string;
  clientLocationContactPhone?: string;
  clientPhone?: string;
  clientLocationContactEmail?: string;
  clientEmail?: string;
  vendors?: Vendor[];
  signedOff?: boolean;
  invoiced?: boolean;
  quoteRequested?: boolean;
  pricingReceived?: boolean;
  photosReceived?: boolean;
  invoiceReceived?: boolean;
  vendorPaid?: boolean;
  vendorPaidDate?: string | null;
  vendorPaymentReference?: string | null;
  nteAmount?: number;
  incurredCost?: number | null;
  incurredCostNote?: string | null;
  proposedCost?: number | null;
  actualCost?: number | null;
}

interface JobContactSidebarProps {
  workOrder: WorkOrder;
  signedOff: boolean;
  invoiced: boolean;
  quoteRequested: boolean;
  pricingReceived: boolean;
  photosReceived: boolean;
  invoiceReceived: boolean;
  vendorPaid: boolean;
  vendorPaidDate?: string | null;
  vendorPaymentReference?: string | null;
  nteAmount?: number;
  incurredCost?: number | null;
  incurredCostNote?: string | null;
  proposedCost?: number | null;
  actualCost?: number | null;
  onSignedOffChange: (checked: boolean) => void;
  onInvoicedChange: (checked: boolean) => void;
  onQuoteRequestedChange: (checked: boolean) => void;
  onPricingReceivedChange: (checked: boolean) => void;
  onPhotosReceivedChange: (checked: boolean) => void;
  onInvoiceReceivedChange: (checked: boolean) => void;
  onVendorPaidChange: (checked: boolean) => void;
  onVendorPaidDateChange: (date: string | null) => void;
  onVendorPaymentReferenceChange: (reference: string) => void;
  onNteChange: (amount: number | null) => void;
  onCostsChange: (costs: {
    incurredCost: number | null;
    incurredCostNote: string | null;
    proposedCost: number | null;
    actualCost: number | null;
  }) => void;
  documentationChecklist?: WorkOrderDocumentationChecklist | null;
}

const formatDatetimeLocal = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const parseDatetimeLocal = (value: string): string | null => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const formatCostTyping = (raw: string): string => {
  const cleaned = raw.replace(/[^0-9.]/g, '');
  if (!cleaned) return '';

  const dotIndex = cleaned.indexOf('.');
  const wholeDigits = (dotIndex === -1 ? cleaned : cleaned.slice(0, dotIndex)).replace(/^0+(?=\d)/, '');
  const whole = wholeDigits || (dotIndex === -1 ? '' : '0');
  if (!whole && dotIndex === -1) return '';

  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (dotIndex === -1) return `$${withCommas}`;

  const fraction = cleaned.slice(dotIndex + 1).replace(/\./g, '').slice(0, 2);
  return `$${withCommas}.${fraction}`;
};

const placeCurrencyCursor = (input: HTMLInputElement, digitsBefore: number) => {
  const formatted = input.value;
  if (digitsBefore <= 0) {
    const start = formatted.startsWith('$') ? 1 : 0;
    input.setSelectionRange(start, start);
    return;
  }

  let seen = 0;
  for (let index = 0; index < formatted.length; index += 1) {
    if (/[0-9]/.test(formatted[index])) seen += 1;
    if (seen >= digitsBefore) {
      input.setSelectionRange(index + 1, index + 1);
      return;
    }
  }

  input.setSelectionRange(formatted.length, formatted.length);
};

const VendorContactDetails: React.FC<{ vendor: Vendor }> = ({ vendor }) => {
  const hasAddress = Boolean(
    vendor.vendorAddress || vendor.vendorCity || vendor.vendorState || vendor.vendorZipCode
  );

  return (
    <>
      {vendor.vendorPhone && (
        <div className="flex items-center space-x-2">
          <Phone className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          <a
            href={`tel:${vendor.vendorPhone.replace(/\D/g, '')}`}
            className="text-sm text-accent-color dark:text-accent-color-400 hover:underline"
          >
            {formatPhoneNumber(vendor.vendorPhone)}
          </a>
        </div>
      )}

      {vendor.vendorEmail && (
        <div className="flex items-center space-x-2">
          <Mail className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          <a
            href={`mailto:${vendor.vendorEmail}`}
            className="text-sm text-accent-color dark:text-accent-color-400 hover:underline"
          >
            {vendor.vendorEmail}
          </a>
        </div>
      )}

      {hasAddress && (
        <div className="flex items-start space-x-2">
          <MapPin className="h-4 w-4 text-gray-500 dark:text-gray-400 mt-0.5" />
          <div className="text-sm text-gray-700 dark:text-gray-300">
            {vendor.vendorAddress && <div>{vendor.vendorAddress}</div>}
            {(vendor.vendorCity || vendor.vendorState || vendor.vendorZipCode) && (
              <div>
                {(vendor.vendorCity || '')}
                {vendor.vendorCity && vendor.vendorState ? ', ' : ''}
                {(vendor.vendorState || '')}
                {vendor.vendorZipCode ? ` ${vendor.vendorZipCode}` : ''}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const JobContactSidebar: React.FC<JobContactSidebarProps> = ({
  workOrder,
  signedOff,
  invoiced,
  quoteRequested,
  pricingReceived,
  photosReceived,
  invoiceReceived,
  vendorPaid,
  vendorPaidDate,
  vendorPaymentReference,
  nteAmount,
  incurredCost,
  incurredCostNote,
  proposedCost,
  actualCost,
  onSignedOffChange,
  onInvoicedChange,
  onQuoteRequestedChange,
  onPricingReceivedChange,
  onPhotosReceivedChange,
  onInvoiceReceivedChange,
  onVendorPaidChange,
  onVendorPaidDateChange,
  onVendorPaymentReferenceChange,
  onNteChange,
  onCostsChange,
  documentationChecklist,
}) => {
  const { hasFeature } = useSubscription();
  const hasVendorCompliance = hasFeature('hasVendorCompliance');
  const [otherVendorsExpanded, setOtherVendorsExpanded] = useState(false);
  const primaryVendor = workOrder.vendors?.find(v => v.isPrimary) || workOrder.vendors?.[0];
  const additionalVendors = (workOrder.vendors ?? []).filter(
    (vendor) => vendor.vendorId !== primaryVendor?.vendorId
  );

  const handleToggleOtherVendors = () => {
    setOtherVendorsExpanded((prev) => !prev);
  };

  const handleOtherVendorsKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggleOtherVendors();
    }
  };

  const documentationItems = documentationChecklist
    ? [
        { label: 'Photos', complete: documentationChecklist.photosComplete },
        { label: 'Vendor invoice', complete: documentationChecklist.vendorInvoiceComplete },
        ...(hasVendorCompliance
          ? [
              { label: 'COI', complete: documentationChecklist.coiComplete },
              { label: 'W-9', complete: documentationChecklist.w9Complete },
            ]
          : []),
      ]
    : [];

  const handleCurrencyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const selection = input.selectionStart ?? input.value.length;
    const digitsBefore = input.value.slice(0, selection).replace(/[^0-9]/g, '').length;
    input.value = formatCostTyping(input.value);
    placeCurrencyCursor(input, digitsBefore);
  };

  const handleNteBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^0-9.]/g, '');
    const num = val ? parseFloat(val) : null;
    e.target.value = num != null ? formatCurrency(num) : '';
    onNteChange(num);
  };

  const savedCosts = () => ({
    incurredCost: incurredCost ?? null,
    incurredCostNote: incurredCostNote?.trim() || null,
    proposedCost: proposedCost ?? null,
    actualCost: actualCost ?? null,
  });

  const handleCostBlur = (
    event: React.FocusEvent<HTMLInputElement>,
    field: 'incurredCost' | 'proposedCost' | 'actualCost'
  ) => {
    const current = savedCosts()[field];
    const trimmed = event.target.value.trim();

    if (trimmed.includes('-')) {
      toast.error('Cost amounts cannot be negative');
      event.target.value = current != null ? formatCurrency(current) : '';
      return;
    }

    const cleaned = trimmed.replace(/[^0-9.]/g, '');
    const parsed = cleaned ? parseFloat(cleaned) : null;
    if (cleaned && Number.isNaN(parsed)) {
      event.target.value = current != null ? formatCurrency(current) : '';
      return;
    }

    const amount = parsed != null ? Math.round(parsed * 100) / 100 : null;
    event.target.value = amount != null ? formatCurrency(amount) : '';
    if (amount === current) return;

    onCostsChange({
      ...savedCosts(),
      [field]: amount,
    });
  };

  const handleIncurredNoteBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const next = event.target.value.trim();
    if (next.length > 200) {
      toast.error('Incurred cost note cannot exceed 200 characters');
      event.target.value = incurredCostNote ?? '';
      return;
    }

    if (next === (incurredCostNote ?? '')) return;

    onCostsChange({
      ...savedCosts(),
      incurredCostNote: next || null,
    });
  };

  const showProposalTotal = incurredCost != null || proposedCost != null;
  const proposalTotal = (incurredCost ?? 0) + (proposedCost ?? 0);

  return (
    <div className="w-full lg:w-80 bg-gray-50 dark:bg-gray-900 lg:border-l border-t lg:border-t-0 border-gray-200 dark:border-gray-700 flex flex-col lg:h-full">
      <div className="p-6 space-y-6 lg:overflow-y-auto flex-1">
        
        {/* Quick Contact Section */}
        <div>
          <h3 className="flex items-center space-x-2 text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
            <Phone className="h-4 w-4" />
            <span>Quick Contact</span>
          </h3>
          
          <div className="space-y-4">
            {/* Client Info */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 space-y-3">
              <div className="flex items-center space-x-2">
                <Building className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <span className="font-medium text-gray-900 dark:text-gray-100">Client: {workOrder.clientName}</span>
              </div>
              
              {(workOrder.clientLocationContactPhone || workOrder.clientPhone) && (
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <a 
                    href={`tel:${(workOrder.clientLocationContactPhone || workOrder.clientPhone || '').replace(/\D/g, '')}`}
                    className="text-sm text-accent-color dark:text-accent-color-400 hover:underline"
                  >
                    {formatPhoneNumber(workOrder.clientLocationContactPhone || workOrder.clientPhone || '')}
                  </a>
                </div>
              )}
              
              {(workOrder.clientLocationContactEmail || workOrder.clientEmail) && (
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <a 
                    href={`mailto:${workOrder.clientLocationContactEmail || workOrder.clientEmail}`}
                    className="text-sm text-accent-color dark:text-accent-color-400 hover:underline"
                  >
                    {workOrder.clientLocationContactEmail || workOrder.clientEmail}
                  </a>
                </div>
              )}
              
              {(workOrder.clientLocationName || workOrder.clientCity || workOrder.clientState || workOrder.clientZipCode) && (
                <div className="flex items-start space-x-2">
                  <MapPin className="h-4 w-4 text-gray-500 dark:text-gray-400 mt-0.5" />
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    {workOrder.clientLocationName && (
                      <div>{workOrder.clientLocationName.replace(' - Primary Location', '')}</div>
                    )}
                    {(workOrder.clientCity || workOrder.clientState || workOrder.clientZipCode) && (
                      <div>
                        {(workOrder.clientCity || '')}{workOrder.clientCity && workOrder.clientState ? ', ' : ''}{(workOrder.clientState || '')}{workOrder.clientZipCode ? ` ${workOrder.clientZipCode}` : ''}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Vendor Info */}
            {primaryVendor && (
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 space-y-3">
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    Vendor: {primaryVendor.vendorName}
                    {primaryVendor.isPrimary && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent-color-100 text-accent-color-800 dark:bg-accent-color-900 dark:text-accent-color-200">
                        Primary
                      </span>
                    )}
                  </span>
                </div>

                <VendorContactDetails vendor={primaryVendor} />

                {additionalVendors.length > 0 && (
                  <div className="pt-3 mt-1 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={handleToggleOtherVendors}
                      onKeyDown={handleOtherVendorsKeyDown}
                      aria-expanded={otherVendorsExpanded}
                      aria-controls="other-vendors-list"
                      aria-label={
                        otherVendorsExpanded
                          ? 'Collapse other vendors'
                          : `Expand ${additionalVendors.length} other vendor${additionalVendors.length === 1 ? '' : 's'}`
                      }
                      tabIndex={0}
                      className="flex w-full items-center gap-2 text-left rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/50 px-1 py-1 -mx-1 transition-colors"
                    >
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {additionalVendors.length} other vendor{additionalVendors.length === 1 ? '' : 's'}
                      </span>
                      <span className="ml-auto shrink-0 text-gray-400 dark:text-gray-500">
                        {otherVendorsExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </span>
                    </button>
                    {otherVendorsExpanded && (
                      <div id="other-vendors-list" className="mt-3 space-y-4">
                        {additionalVendors.map((vendor) => (
                          <div
                            key={vendor.vendorId}
                            className="space-y-2 pt-3 border-t border-gray-100 dark:border-gray-700 first:border-t-0 first:pt-0"
                          >
                            <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
                              {vendor.vendorName}
                            </span>
                            <VendorContactDetails vendor={vendor} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Quick Info Section */}
        <div>
          <h3 className="flex items-center space-x-2 text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
            <Receipt className="h-4 w-4" />
            <span>Quick Info</span>
          </h3>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 space-y-4">
            {/* Checkboxes */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  checked={signedOff} 
                  onCheckedChange={onSignedOffChange}
                  className="data-[state=checked]:bg-accent-color data-[state=checked]:border-accent-color"
                />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  Signed Off
                </label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  checked={invoiced} 
                  onCheckedChange={onInvoicedChange}
                  className="data-[state=checked]:bg-accent-color data-[state=checked]:border-accent-color"
                />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  Invoiced
                </label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  checked={quoteRequested}
                  onCheckedChange={onQuoteRequestedChange}
                  className="data-[state=checked]:bg-accent-color data-[state=checked]:border-accent-color"
                />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  Quote Requested
                </label>
              </div>

              {/* Separator */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3"></div>

              {/* New Received Checkboxes */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  checked={pricingReceived}
                  onCheckedChange={onPricingReceivedChange}
                  className="data-[state=checked]:bg-accent-color data-[state=checked]:border-accent-color"
                />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  Pricing Received
                </label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  checked={photosReceived}
                  onCheckedChange={onPhotosReceivedChange}
                  className="data-[state=checked]:bg-accent-color data-[state=checked]:border-accent-color"
                />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  Photos Received
                </label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  checked={invoiceReceived}
                  onCheckedChange={onInvoiceReceivedChange}
                  className="data-[state=checked]:bg-accent-color data-[state=checked]:border-accent-color"
                />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  Invoice Received
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox 
                  checked={vendorPaid}
                  onCheckedChange={onVendorPaidChange}
                  className="data-[state=checked]:bg-accent-color data-[state=checked]:border-accent-color"
                />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  Vendor Paid
                </label>
              </div>

              {vendorPaid && (
                <div className="ml-6 space-y-3 border-l-2 border-gray-200 dark:border-gray-700 pl-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Paid Date
                    </label>
                    <Input
                      type="datetime-local"
                      className="w-full text-sm"
                      defaultValue={formatDatetimeLocal(vendorPaidDate)}
                      key={`vendor-paid-date-${vendorPaidDate ?? 'empty'}`}
                      onBlur={(e) => onVendorPaidDateChange(parseDatetimeLocal(e.target.value))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Check / Ref #
                    </label>
                    <Input
                      type="text"
                      className="w-full text-sm"
                      defaultValue={vendorPaymentReference ?? ''}
                      key={`vendor-payment-ref-${vendorPaymentReference ?? 'empty'}`}
                      placeholder="Check or reference number"
                      onBlur={(e) => onVendorPaymentReferenceChange(e.target.value.trim())}
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* NTE Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                NTE:
              </label>
              <Input
                key={`nte-${nteAmount ?? 'empty'}`}
                type="text"
                className="w-full"
                defaultValue={nteAmount != null ? formatCurrency(nteAmount) : ''}
                onChange={handleCurrencyChange}
                onBlur={handleNteBlur}
                placeholder="$0.00"
              />
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-3">
              <div>
                <label htmlFor="incurred-cost" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Incurred cost
                </label>
                <Input
                  id="incurred-cost"
                  key={`incurred-${incurredCost ?? 'empty'}`}
                  type="text"
                  inputMode="decimal"
                  className="w-full"
                  defaultValue={incurredCost != null ? formatCurrency(incurredCost) : ''}
                  onChange={handleCurrencyChange}
                  onBlur={(event) => handleCostBlur(event, 'incurredCost')}
                  placeholder="$0.00"
                  aria-describedby="incurred-cost-help"
                />
                <p id="incurred-cost-help" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Charged even if the proposal is declined.
                </p>
              </div>
              <div>
                <label htmlFor="incurred-cost-note" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  What is this fee?
                </label>
                <Input
                  id="incurred-cost-note"
                  key={`incurred-note-${incurredCostNote ?? 'empty'}`}
                  type="text"
                  className="w-full text-sm"
                  maxLength={200}
                  defaultValue={incurredCostNote ?? ''}
                  onBlur={handleIncurredNoteBlur}
                  placeholder="Diagnostic fee"
                  aria-label="Incurred cost note"
                />
              </div>
              <div>
                <label htmlFor="proposed-cost" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Proposed cost
                </label>
                <Input
                  id="proposed-cost"
                  key={`proposed-${proposedCost ?? 'empty'}`}
                  type="text"
                  inputMode="decimal"
                  className="w-full"
                  defaultValue={proposedCost != null ? formatCurrency(proposedCost) : ''}
                  onChange={handleCurrencyChange}
                  onBlur={(event) => handleCostBlur(event, 'proposedCost')}
                  placeholder="$0.00"
                  aria-describedby="proposed-cost-help"
                />
                <p id="proposed-cost-help" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Vendor estimate for the work.
                </p>
              </div>
              <div>
                <label htmlFor="actual-cost" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Actual cost
                </label>
                <Input
                  id="actual-cost"
                  key={`actual-${actualCost ?? 'empty'}`}
                  type="text"
                  inputMode="decimal"
                  className="w-full"
                  defaultValue={actualCost != null ? formatCurrency(actualCost) : ''}
                  onChange={handleCurrencyChange}
                  onBlur={(event) => handleCostBlur(event, 'actualCost')}
                  placeholder="$0.00"
                  aria-describedby="actual-cost-help"
                />
                <p id="actual-cost-help" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Final amount when the invoice is processed.
                </p>
              </div>
              {showProposalTotal && (
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Submit with proposal</span>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(proposalTotal)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {documentationChecklist && (
          <div>
            <h3 className="flex items-center space-x-2 text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
              <ClipboardCheck className="h-4 w-4" />
              <span>Documentation</span>
              {documentationChecklist.isComplete ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  Complete
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                  {documentationChecklist.blockingIssueCount} issue{documentationChecklist.blockingIssueCount === 1 ? '' : 's'}
                </span>
              )}
            </h3>

            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 space-y-2">
              {documentationChecklist.primaryVendorName && hasVendorCompliance && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Primary vendor: {documentationChecklist.primaryVendorName}
                </p>
              )}
              {documentationItems.map((item) => (
                <div key={item.label} className="flex items-center gap-2 text-sm">
                  {item.complete ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                  <span className={getDocumentationItemClasses(item.complete)}>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobContactSidebar;
